/** The inline form for scheduling a new rate for one role. */
import { useEffect, useRef } from 'react';

import type { PayRateRole, SetPayRateInput } from '../../../api/staff-pay-rates';

import { PAY_RATE_ROLE_LABELS } from './pay-rate-format';
import PayRateField from './PayRateField';
import SetRateActions from './SetRateActions';
import { useSetRateDraft } from './use-set-rate-draft';
import type { WriteResult } from './use-staff-pay-rates';

export type SetRateSubmit = (input: SetPayRateInput) => Promise<WriteResult>;

export default function SetRateForm({
  role,
  today,
  earliest,
  busy,
  onSubmit,
  onClose,
}: {
  role: PayRateRole;
  today: string;
  earliest: string;
  busy: boolean;
  onSubmit: SetRateSubmit;
  onClose: () => void;
}) {
  const draft = useSetRateDraft({ role, today, earliest, busy, onSubmit, onSaved: onClose });
  const amountRef = useRef<HTMLInputElement>(null);
  useEffect(() => amountRef.current?.focus(), []);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void draft.save();
      }}
    >
      <fieldset className="mt-3 min-w-0 border-0 p-0">
        <legend className="mb-1 text-xs font-medium text-stone-700 dark:text-stone-300">
          {PAY_RATE_ROLE_LABELS[role]} rate
        </legend>
        <div className="flex flex-wrap items-end gap-3">
          <PayRateField
            id={`amount-${role}`}
            label="Amount per shift ($)"
            inputRef={amountRef}
            inputMode="decimal"
            placeholder="e.g. 250"
            className="w-28"
            value={draft.amount}
            error={draft.amountError}
            onChange={(e) => draft.setAmount(e.target.value)}
          />
          <PayRateField
            id={`date-${role}`}
            label="Effective from"
            type="date"
            min={earliest}
            value={draft.date}
            error={draft.dateError}
            onChange={(e) => draft.setDate(e.target.value)}
          />
          <SetRateActions canSave={draft.canSave} busy={busy} onCancel={onClose} />
        </div>
        {draft.otherErrors.length > 0 && (
          <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-400">
            {draft.otherErrors.join(' ')}
          </p>
        )}
      </fieldset>
    </form>
  );
}
